export const delay = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

// https://dev.to/bwca/create-a-debounce-function-from-scratch-in-typescript-560m
export function debounce<A = unknown, R = void>(
  fn: (args: A) => R,
  ms: number,
): [(args: A) => Promise<R>, () => void] {
  let timer: NodeJS.Timeout;

  const debouncedFunc = (args: A): Promise<R> =>
    new Promise((resolve) => {
      if (timer) {
        clearTimeout(timer);
      }

      timer = setTimeout(() => {
        resolve(fn(args));
      }, ms);
    });

  const teardown = () => clearTimeout(timer);

  return [debouncedFunc, teardown];
}

/**
 * Formats a string or number input to a properly toFixed number, if it has no decimals, it just returns the number itself
 * @param input a numeric string or number
 * @param decimals number of decimals to use in toFixed
 * @returns numeric string
 */
export const formatNumber = (input: string | number, decimals = 2): string => {
  const number = typeof input === 'string' ? Number.parseFloat(input) : input;
  return Number.isNaN(number)
    ? 'NaN'
    : Number.isInteger(number)
    ? number.toString()
    : number.toFixed(decimals);
};

/**
 * Takes an error message with underscores and converts it to a prettified format.
 *
 * @param {string} errorMessage - The error message to be prettified.
 * @returns {string} The prettified error message with capitalized words separated by spaces.
 *
 * @example
 * const uglyErrorMessage = 'ideal_session is required';
 * const prettyErrorMessage = prettifyErrorMessage(uglyErrorMessage);
 * console.log(prettyErrorMessage);
 * // Output: 'Ideal Session is required'
 */
export function prettifyErrorMessage(errorMessage: string): string {
  return errorMessage
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function scaleWidth(fontsize: number) {
  const deviceWidth = window?.innerWidth ?? screen.width;
  return Math.min(fontsize * deviceWidth / 390, fontsize)
}